import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Link, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LaunchIcon from '@mui/icons-material/Launch';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';

interface FooterProps {
  fixed?: boolean;
  hideLinks?: boolean;
}

const Footer: React.FC<FooterProps> = ({ fixed = false, hideLinks = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const handleLogout = async () => {
    // AWS Cognitoからのログアウト処理とローカルストレージ/セッションストレージのクリア
    await authService.logout();
    // Reduxの状態を更新
    dispatch(logout());
    // サインアウト時のパスを保持し、再サインインで復帰できるようにする
    navigate('/login', { state: { from: location.pathname + location.search } });
  };
  
  return (
    <Box
      component="footer"
      className="footer"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '100%',
        marginTop: 'auto',
        ...(fixed && {
          position: 'fixed',
          bottom: 0,
          left: 0,
          zIndex: theme.zIndex.drawer - 1,
        }),
      }}
    >
      {/* 上段：機能リンク（黒背景） */}
      {!hideLinks && (
        <Box
          className="links-bar"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            minHeight: '54px',
            height: { xs: 'auto', sm: '54px' },
            backgroundColor: '#000000',
          }}
        >
          <Box 
            className="links"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              padding: { xs: '10px', sm: '0' },
            }}
          >
            {isAuthenticated && (
              <>
                <Link
                  component="button"
                  onClick={handleLogout}
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    letterSpacing: '0.1em',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  サインアウト
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/profile/change-password" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    letterSpacing: '0.1em',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  パスワードの変更
                </Link>
              </>
            )}
            <Link 
              component={RouterLink} 
              to="https://www.revicglobal.com/" 
              target="_blank"
              sx={{ 
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.1em',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              運営会社
              <LaunchIcon sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>
            <Link 
              component={RouterLink} 
              to="https://www.revicglobal.com/privacypolicy/" 
              target="_blank"
              sx={{ 
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.1em',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              プライバシーポリシー
              <LaunchIcon sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>
          </Box>
        </Box>
      )}
      
      {/* 下段：コピーライト（グレー背景） */}
      <Box
        className="copyright"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px',
          width: '100%',
          height: '55px',
          backgroundColor: '#666666',
        }}
      >
        <Typography 
          sx={{
            color: theme.palette.smartSkillTalk.textLight,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.1em',
          }}
        >
          © 2025 REVIC Global Co., Ltd.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
